import React from 'react';
import { 
  RiWifiLine, 
  RiDatabase2Line, 
  RiShieldCheckLine, 
  RiCheckboxCircleLine,
  RiInformationLine,
  RiSettings3Line,
  RiFileList3Line,
  RiSmartphoneLine,
  RiGlobalLine,
  RiTimeLine,
  RiLockLine
} from 'react-icons/ri';
import SEO from '../components/SEO';
import './Features.css';

const Features = () => {
  const features = [
    {
      icon: RiWifiLine,
      title: 'Network Capture',
      description: 'When a SIM is inserted, the network captures the device\'s unique 15-digit IMEI number automatically.',
      gradient: 'gradient-1'
    },
    {
      icon: RiDatabase2Line,
      title: 'Database Check',
      description: 'The system verifies the IMEI against the central BTRC database to check registration status.',
      gradient: 'gradient-2'
    },
    {
      icon: RiShieldCheckLine,
      title: 'Verification',
      description: 'Device authenticity is confirmed, checking if it\'s legal, not stolen, or reported as cloned.',
      gradient: 'gradient-3'
    },
    {
      icon: RiCheckboxCircleLine,
      title: 'Access Control',
      description: 'Network access is granted or denied based on verification results for security purposes.',
      gradient: 'gradient-4'
    }
  ];

  const processSteps = [
    {
      number: 1,
      icon: RiSmartphoneLine,
      title: 'Device Registration',
      description: 'All mobile devices are registered in the central NEIR database with their IMEI numbers, MSISDN, and user NID.'
    },
    {
      number: 2,
      icon: RiGlobalLine,
      title: 'Network Integration',
      description: 'NEIR connects with Equipment Identity Registers (EIRs) of all Cellular Mobile Phone Operators (CMPO).'
    },
    {
      number: 3,
      icon: RiTimeLine,
      title: 'Real-time Monitoring',
      description: 'The system monitors CMPO EIRs continuously to ensure compliance and security across all networks.'
    },
    {
      number: 4,
      icon: RiLockLine,
      title: 'Access Control',
      description: 'Based on verification results, network access is controlled to prevent unauthorized device usage.'
    }
  ];

  return (
    <>
      <SEO
        title="NEIR Features - System Architecture & Capabilities | BTRC Bangladesh Government"
        description="Explore the core features and system architecture of the National Equipment Identity Register (NEIR) by Bangladesh Telecommunication Regulatory Commission (BTRC). Learn about network capture, database verification, device authentication, access control, and real-time monitoring. Official government mobile device management system features."
        keywords="NEIR features, NEIR BTRC features, NEIR Bangladesh features, NEIR.gov.bd features, NEIR.btrc.gov.bd features, neir.btrc.gov.bd features, National Equipment Identity Register features, BTRC NEIR system features, mobile device verification features, IMEI verification system, network capture system, database verification, device authentication system, access control system, real-time monitoring, government mobile database features, BTRC system capabilities, mobile device registration features, government telecommunications features, official mobile database system, device identity verification, mobile network security features, government mobile services features, Bangladesh telecom regulatory features, mobile device management features, official device registration features, government mobile database capabilities, NEIR system architecture, mobile device compliance features, government telecommunications infrastructure, official mobile verification features, Bangladesh mobile network security, government mobile database system features, NEIR technical features, mobile device security features, neir.btrc.gov.bd features, www.neir.btrc.gov.bd features, neir portal features, btrc neir portal features, neir btrc website features, neir btrc official features, neir btrc system features, neir btrc database features, neir btrc service features, neir btrc bangladesh features, neir btrc gov features, neir btrc government features, .gov.bd features, gov.bd domain features, government domain Bangladesh features, official government domain features, btrc.gov.bd features, neir.btrc.gov.bd features, www.btrc.gov.bd features, www.neir.btrc.gov.bd features"
        ogTitle="NEIR Features - System Architecture & Capabilities | BTRC Bangladesh"
        ogDescription="Explore the core features and system architecture of the National Equipment Identity Register (NEIR) by BTRC."
        ogImage="/images/btrc-logo.png"
        canonicalUrl="https://neir.btrc.gov.bd/features"
      />
      <div className="features-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="header-icon-wrapper">
              <RiSettings3Line className="header-icon" />
            </div>
            <h1 className="page-title">
              <span className="gradient-text">NEIR Features</span>
            </h1>
            <p className="page-subtitle">
              Discover the powerful features and capabilities of the National Equipment Identity Register system
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-icon-wrapper">
              <RiInformationLine className="section-icon" />
            </div>
            <h2 className="section-title">
              <span className="gradient-text-white">How NEIR Works</span>
            </h2>
            <p className="section-subtitle">
              Understanding the verification process and system architecture
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="feature-card">
                  <div className={`feature-icon ${feature.gradient}`}>
                    <IconComponent />
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Flow Section */}
      <section className="process-section section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-icon-wrapper-dark">
              <RiFileList3Line className="section-icon-dark" />
            </div>
            <h2 className="section-title">
              <span className="gradient-text">System Architecture</span>
            </h2>
            <p className="section-subtitle-dark">
              Step-by-step process of how NEIR integrates with mobile networks
            </p>
          </div>
          <div className="process-flow">
            {processSteps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div key={index} className="process-step">
                  <div className="step-number">{step.number}</div>
                  <div className="step-icon-wrapper">
                    <StepIcon className="step-icon" />
                  </div>
                  <div className="step-content">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Features;

